import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { KeyRound } from "lucide-react";

export default function ChangePasswordDialog({ open, onClose }) {
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm_password: "" });
  const [busy, setBusy] = useState(false);

  const reset = () => setForm({ current_password: "", new_password: "", confirm_password: "" });

  const submit = async (e) => {
    e.preventDefault();
    if (form.new_password.length < 8) return toast.error("New password must be at least 8 characters.");
    if (form.new_password !== form.confirm_password) return toast.error("New passwords don't match.");
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: form.current_password, new_password: form.new_password });
      toast.success("Password changed. Next login will use the new password.");
      reset();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't change password.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { reset(); onClose(); } }}>
      <DialogContent data-testid="change-password-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-serif-display text-2xl flex items-center gap-2" style={{ color: "var(--ink)" }}>
            <KeyRound size={18} style={{ color: "var(--ember)" }} /> Change password
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-2">
          <div className="space-y-1">
            <Label className="text-[0.6rem] uppercase tracking-[0.18em]" style={{ color: "var(--muted)" }}>Current password</Label>
            <Input required type="password" value={form.current_password} onChange={(e) => setForm({ ...form, current_password: e.target.value })} data-testid="cp-current" />
          </div>
          <div className="space-y-1">
            <Label className="text-[0.6rem] uppercase tracking-[0.18em]" style={{ color: "var(--muted)" }}>New password</Label>
            <Input required type="password" value={form.new_password} onChange={(e) => setForm({ ...form, new_password: e.target.value })} data-testid="cp-new" />
            <p className="text-[0.65rem]" style={{ color: "var(--muted)" }}>At least 8 characters.</p>
          </div>
          <div className="space-y-1">
            <Label className="text-[0.6rem] uppercase tracking-[0.18em]" style={{ color: "var(--muted)" }}>Confirm new password</Label>
            <Input required type="password" value={form.confirm_password} onChange={(e) => setForm({ ...form, confirm_password: e.target.value })} data-testid="cp-confirm" />
          </div>
          <DialogFooter className="mt-4">
            <button type="button" onClick={() => { reset(); onClose(); }} className="btn-ghost" data-testid="cp-cancel">Cancel</button>
            <button type="submit" disabled={busy} className="btn-primary" data-testid="cp-submit">
              {busy ? "Saving…" : "Update password"}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
